import React from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import type { RootState } from "../store/store";

import { FREELANCER_HOME_PAGE } from "../views/freelancer/routes";
import { COLABORADOR_HOME, COLABORADOR_PRIMEIRO_ACESSO } from "../views/colaborador/routes";

import { isTokenValid } from "../utils/isTokenValid";
import { staffHomeFromToken } from "../utils/staffHome";

type Props = { children: React.ReactElement };

/**
 * Guard das telas públicas de entrada (`/entrar` e o cadastro de freelancer).
 *
 * Substitui o `PublicRoute` e o `EmployeePublicRoute`: com uma tela só para os
 * três mundos, não faz mais sentido um guard que enxerga apenas um deles — quem
 * já tem sessão vai para a casa dela, qualquer que seja o mundo.
 *
 * A ordem (colaborador → freelancer → staff) é a MESMA do `DefaultRedirect`: da
 * sessão mais específica para a mais genérica. Divergir faria a mesma pessoa
 * parar em lugares diferentes conforme a porta pela qual entrou.
 *
 * Só funciona porque os três slices gravam `accessToken`. Enquanto o freelancer
 * gravava `token`, era um trecho perdido no `loginChoice.tsx` que fazia este
 * papel, lendo `localStorage` à mão (BUG-006 e BUG-011).
 */
const LoginPublicRoute: React.FC<Props> = ({ children }) => {
  const rehydrated = useSelector((s: RootState) => !!(s as any)._persist?.rehydrated);

  const userToken = useSelector((s: RootState) => s.auth?.accessToken);
  const freelancerToken = useSelector((s: RootState) => s.freelancerAuth?.accessToken);
  const employeeToken = useSelector((s: RootState) => s.employeeAuth?.accessToken);
  const employeeIsFirstLogin = useSelector((s: RootState) => s.employeeAuth?.isFirstLogin);

  if (!rehydrated) return null;

  if (isTokenValid(employeeToken)) {
    return (
      <Navigate
        to={employeeIsFirstLogin ? COLABORADOR_PRIMEIRO_ACESSO() : COLABORADOR_HOME()}
        replace
      />
    );
  }

  if (isTokenValid(freelancerToken)) {
    return <Navigate to={FREELANCER_HOME_PAGE()} replace />;
  }

  if (isTokenValid(userToken)) {
    return <Navigate to={staffHomeFromToken(userToken)} replace />;
  }

  return children;
};

export default LoginPublicRoute;
